/**
 * Adapter manuel pour saisie de poids
 * Utilisé comme fallback quand aucune balance n'est disponible
 * ou pour tests sans hardware
 */

import { ScaleAdapter, WeightReading, ScaleError } from './ScaleAdapter';
import { logger } from '../../utils/logger';

export class ManualScaleAdapter implements ScaleAdapter {
  readonly modelName = 'Saisie manuelle';
  readonly supportsAutoConnect = false;

  private connected = false;
  private subscribers: Array<(reading: WeightReading) => void> = [];
  private currentWeight: WeightReading = {
    weight: 0,
    stable: true,
    unit: 'g',
    timestamp: Date.now(),
  };

  /**
   * "Connecter" (juste marquer comme prêt)
   */
  async connect(): Promise<void> {
    this.connected = true;
    logger.log('[ManualScaleAdapter] Mode manuel activé');
  }

  /**
   * Déconnecter
   */
  async disconnect(): Promise<void> {
    this.connected = false;
    this.currentWeight = {
      weight: 0,
      stable: true,
      unit: 'g',
      timestamp: Date.now(),
    };
    logger.log('[ManualScaleAdapter] Mode manuel désactivé');
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Lire le poids actuel
   */
  async readWeight(): Promise<WeightReading> {
    if (!this.isConnected()) {
      throw new ScaleError('Adapter manuel non initialisé', 'NOT_CONNECTED');
    }

    return { ...this.currentWeight };
  }

  /**
   * Tarrer (remettre à zéro)
   */
  async tare(): Promise<void> {
    if (!this.isConnected()) {
      throw new ScaleError('Adapter manuel non initialisé', 'NOT_CONNECTED');
    }

    this.setWeight(0);
  }

  /**
   * S'abonner aux changements de poids
   */
  subscribe(callback: (reading: WeightReading) => void): () => void {
    this.subscribers.push(callback);

    // Retourner fonction de désabonnement
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Définir manuellement un poids (appelé par l'interface utilisateur)
   * @param weight Poids en grammes
   */
  setWeight(weight: number): void {
    this.currentWeight = {
      weight,
      stable: true,
      unit: 'g',
      timestamp: Date.now(),
    };

    // Notifier tous les abonnés
    this.subscribers.forEach((callback) => callback({ ...this.currentWeight }));
  }

  /**
   * Définir un poids en kilogrammes
   * @param weightKg Poids en kilogrammes
   */
  setWeightKg(weightKg: number): void {
    this.setWeight(weightKg * 1000);
  }
}
